import React, { useState, useRef, useEffect } from 'react';
import { db, auth } from '../firebase';
import { collection, addDoc, serverTimestamp, doc, setDoc, getDoc, getDocs, query, where, updateDoc, arrayUnion } from 'firebase/firestore';
import { LOCATIONS_BY_RAILROAD, AGREEMENT_RULES, RAILROADS, COMMITTEE_LOOKUP, AGREEMENT_LOOKUP } from '../data/agreementData';

import { resolveScopeKey } from '../data/scopeKeyData';
import { resolveLocalCommittee } from '../data/routing';
import { nextClaimNumber } from '../utils/claimNumber';
import { buildClaimPayload, computeAmountClaimed } from '../data/claimPayload';
import { profilePatch, filerIsClaimant } from '../data/memberProfile';
import { pickResumableDraft, multipleDraftsNotice, draftIntoForm, droppedDraftKeys } from '../data/claimDrafts';
import DictationButton from './DictationButton';
import EligibilityPayablePanel from '../components/EligibilityPayablePanel'; import HowToPanel from '../components/HowToPanel';   // Bob 2026-10-15: the how-to, on this line so no cited line moves
import ClaimBasisBoxes from '../components/ClaimBasisBoxes';
import RuleBasisPicker from './RuleBasisPicker';
import ClaimLanguageHelper from '../components/ClaimLanguageHelper';   // ruling 265: spelling, grammar, claim language
import { getFunctions, httpsCallable } from 'firebase/functions';   // section 540: spelling and grammar, automatic
import { factsFromClaim } from '../data/claimLanguage';
import { needsWorkKind, workKindChoices, explanation as workKindExplanation, applyWorkKind } from '../data/workKind';   // section 536: the what-work box
import ExhibitFileAttach from '../components/ExhibitFileAttach';
import { suggestExhibits } from '../data/suggestedExhibits';   // ruling 250: Box 5 suggests exhibits from the claim's own facts
import { rosterQueryFor, pickApplication, factsFromApplication, rosterNotice } from '../data/claimantRosterFacts';   // Bob 2026-10-17: the member's roster facts from his application
import { attachStagedExhibits } from '../data/claimDocumentClient';   // section 412 Part B: attach exhibit files at the exhibits step
import { filerCopy, saveCopy, RULING_271_FILER_COPY } from '../data/filedCopy';   // ruling 271: the filer keeps a copy
import useSeatsOfRecord from '../hooks/useSeatsOfRecord';   // Record 609 (ruling 294): the Local Chairperson seat of record
import { recordCarriageAtFiling } from '../data/carriageAtFiling';   // Record 609 (ruling 294): carriage recorded at filing
import { canonicalizeAmtrakRules } from '../data/ruleCatalog';
import { emailProblem, phoneProblem } from '../data/contactCheck';
import { rosterLocationsFor } from '../data/seniorityRosters';
import { getSuggestion } from '../data/suggestedAnswers';
import { sanitizeMatchedRules, hasRuleCatalog } from '../data/ruleSourcing';
import { claimPatch } from '../data/claimWrite';
import { collectBox3Answers, box3AnswersToKeys } from '../data/box3Answers';   // section 510: Box 3 answers are saved
// RULING 261 (2026-09-29): Box 1 asks the kind of claim first, then short questions for that kind. Every
// addition below is gated on BOX1_GUIDED; with it false this screen is today's screen (see box1Guided.js).
import { BOX1_GUIDED, claimKindGroups, narrowRules, guidedQuestions, guidedSuggestion, FACTS_LINE, missingChecklist, cleanExhibitLabel, PRESENTMENT_OMIT, PAYABLE_OMIT } from '../data/box1Guided';
import { claimKind, claimKindPicker, groupOfKind } from '../data/box1Guided';   // Record 603: the two-step category picker
import { barsFor, BAR_HEADING, BAR_NOTE } from '../data/claimBars';   // Record 600 (claim categories, Bob 10/7): what the agreement bars
import { draftStatementOfClaim } from '../data/penaltyClaim';   // ruling 258: the Statement of Claim shown beside her words
import { memberBasisLabel } from '../data/memberBasis';   // Record 612 (ruling 204): the fixed label on a member's suggested basis
import MoreSection from '../components/MoreSection';   // ruling 276 screen 2 (2026-10-16): Box 4 behind More
import { ANALYZE_URL } from '../installation';   // ruling 312: the functions host comes from the installation
import { agreementFamily } from '../data/agreementRegistry';   // ruling 312 phase 1 (d)
import { DIVISIONS, divisionOfCraft, craftsInDivision, wordsFor } from '../data/craftTerms';   // section 807
import DivisionButtons from '../components/DivisionButtons';

const CLOUD_FUNCTION_URL = ANALYZE_URL;

const _today = new Date();
const TODAY_DATE = `${_today.getFullYear()}-${String(_today.getMonth()+1).padStart(2,'0')}-${String(_today.getDate()).padStart(2,'0')}`;

const STEPS = [
  { id: 'who', label: 'Who are you?' },
  { id: 'when', label: 'When did it happen?' },
  { id: 'parties', label: 'Who was involved?' },
  { id: 'what', label: 'What happened?' },
  { id: 'preview', label: 'Review & Submit' },
];


// Box 3 question sets by violation type
const QUESTION_SETS = {
  overtime: [
    { id: 'q_datetime', q: 'What was the exact date and time of the overtime incident?' },
    { id: 'q_manager', q: 'What is the name and title of the manager who made the overtime assignment?' },
    { id: 'q_offered', q: 'Were you offered the overtime before being forced to work it?' },
    { id: 'q_seniority', q: 'What was your seniority standing and availability status at the time?' },
    { id: 'q_witnesses', q: 'Were any witnesses present? If so, who?' },
  ],
  seniority: [
    { id: 'q_datetime', q: 'What was the exact date and time of the seniority violation?' },
    { id: 'q_manager', q: 'What is the name and title of the manager who made the improper assignment?' },
    { id: 'q_seniority', q: 'What is your seniority date and standing on the applicable roster?' },
    { id: 'q_conversation', q: 'Did you notify management of the violation at the time? What was said?' },
    { id: 'q_witnesses', q: 'Were any witnesses present? If so, who?' },
  ],
  bereavement: [
    { id: 'q_datetime', q: 'What was the date of death and when did you notify the carrier?' },
    { id: 'q_relation', q: 'What is your relationship to the deceased? (spouse, parent, child, sibling, grandparent)' },
    { id: 'q_request', q: 'How did you submit your bereavement request — verbal, written, or electronic? To whom?' },
    { id: 'q_denial', q: 'What reason, if any, did the carrier give for the denial?' },
    { id: 'q_notice', q: 'Did you provide the required advance notice as specified in the agreement?' },
    { id: 'q_witnesses', q: 'Were any witnesses present to your request or the denial?' },
  ],
  personal_leave: [
    { id: 'q_datetime', q: 'What date did you request and what date were you denied?' },
    { id: 'q_request', q: 'How did you submit the request — verbal, written, or electronic? To whom?' },
    { id: 'q_notice', q: 'Did you provide the required advance notice? When exactly?' },
    { id: 'q_denial', q: 'What reason, if any, were you given for the denial?' },
    { id: 'q_junior', qKey: 'quickFileJuniorQuestion' },   // section 807: worded for the claim's division
    { id: 'q_witnesses', q: 'Were any witnesses present to your request or the denial?' },
  ],
  abolishment: [
    { id: 'q_datetime', q: 'What was the date your position was abolished or the notice was given?' },
    { id: 'q_notice', q: 'Did the carrier provide the required advance notice? How much notice was given?' },
    { id: 'q_work', q: 'What happened to the work from your abolished position — where was it transferred?' },
    { id: 'q_displacement', q: 'Were you afforded the right to exercise seniority to displace onto another position?' },
    { id: 'q_manager', q: 'What is the name and title of the manager who handled the abolishment?' },
    { id: 'q_witnesses', q: 'Were any witnesses present? If so, who?' },
  ],
  default: [
    { id: 'q_datetime', q: 'What was the exact date and time of the incident?' },
    { id: 'q_manager', q: 'What is the name and title of the manager involved?' },
    { id: 'q_conversation', q: 'How did the conversation take place and what was said?' },
    { id: 'q_junior', q: 'Were any other employees directly involved? Names and roster numbers?' },
    { id: 'q_seniority', q: 'What was your availability and seniority standing at the time?' },
    { id: 'q_witnesses', q: 'Were any witnesses present? If so, who?' },
  ],
};

// Section 807: a question carrying a qKey is worded for the claim's own division.
const worded = (set, craft) => set.map((x) => (x.qKey ? { ...x, q: wordsFor(craft)[x.qKey] } : x));
function getQuestionSet(matchedRules, craft) {
  return worded(getQuestionSetRaw(matchedRules), craft);
}
function getQuestionSetRaw(matchedRules) {
  if (!matchedRules || matchedRules.length === 0) return QUESTION_SETS.default;
  const titles = matchedRules.map(r => (r.rule + ' ' + (r.fullTitle || '') + ' ' + (r.relevance || '')).toLowerCase()).join(' ');
  if (titles.includes('bereavement') || titles.includes('death') || titles.includes('funeral')) return QUESTION_SETS.bereavement;
  if (titles.includes('personal') || titles.includes('leave') || titles.includes('personal day')) return QUESTION_SETS.personal_leave;
  if (titles.includes('abolish') || titles.includes('position abolished')) return QUESTION_SETS.abolishment;
  if (titles.includes('overtime') || titles.includes('forced') || titles.includes('extra service')) return QUESTION_SETS.overtime;
  if (titles.includes('seniority') || titles.includes('junior') || titles.includes('senior')) return QUESTION_SETS.seniority;
  return QUESTION_SETS.default;
}

// Section 510: every Box 3 question the member may have answered - the set on screen first, then any other set's
// questions (a rule change can swap the set after he has answered; his answer is still his and is still saved).
function allBox3Questions(matchedRules, craft) {
  const cur = getQuestionSet(matchedRules, craft);
  const ids = new Set(cur.map(x => x.id));
  const rest = [];
  Object.values(QUESTION_SETS).forEach(set => worded(set, craft).forEach(x => { if (!ids.has(x.id)) { ids.add(x.id); rest.push(x); } }));
  return cur.concat(rest);
}

// Tier 1 auto-answer: source a Box 3 question from structured data the claim already
// holds. Returns { value, complete } for questions we can answer deterministically,
// otherwise null (those stay open for the claimant / later text extraction).
function deriveAnswer(id, claim, user) {
  const f = k => (claim && claim[k] != null ? String(claim[k]).trim() : '');
  const p = k => (user && user[k] != null ? String(user[k]).trim() : '');
  if (id === 'q_datetime') {
    const d = f('incidentDate'), t = f('incidentTime12') || f('incidentTime24');
    if (d && t) return { value: `${d} at ${t}`, complete: true };
    if (d) return { value: d, complete: true };
    return null;
  }
  if (id === 'q_witnesses') {
    const w = f('witnesses');
    return w ? { value: w, complete: true } : null;
  }
  if (id === 'q_junior') {
    const n = f('violatorName'), r = f('violatorRosterNumber');
    if (n && r) return { value: `${n} (roster ${r})`, complete: true };
    if (n) return { value: n, complete: false };
    return null;
  }
  if (id === 'q_seniority') {
    // Bob 2026-10-17: the CLAIM's roster facts first (filled from the member's application, or typed); the signed-in
    // profile only when the filer is the claimant (the caller passes no user otherwise).
    const sd = f('seniorityDate') || p('seniorityDate'), rn = f('rosterNumber') || p('rosterNumber');
    if (sd && rn) return { value: `Seniority date ${sd}, roster ${rn}`, complete: true };
    if (sd) return { value: `Seniority date ${sd}`, complete: false };
    return null;
  }
  return null;
}

// SECTION 528 (2026-09-30, claim 0013 on the call with Eckert): Quick File assumed the filer IS the claimant and
// pre-filled "Your Name" with the signed-in person - so a General Chairperson filing for Mr. Coyle produced a claim
// of "Christian Eckert". An officer now says first whom the claim is for; for a member, the claimant fields start
// empty and are labelled as the claimant's, and nothing of the officer's own profile is carried into the claim.
const OFFICER_ROLES = ['local_chair', 'general_chair', 'admin'];
const selfIdentity = (user) => ({
  claimantName: user?.name || '', claimantEmail: user?.email || '', claimantPhone: user?.phone || '',
  claimantStreet: user?.street || '', claimantCity: user?.city || '', claimantState: user?.state || '', claimantZip: user?.zip || '',
  craft: user?.craft || '', railroad: user?.railroad || '', workLocation: user?.workLocation || '',
});
const blankIdentity = () => ({ claimantName: '', claimantEmail: '', claimantPhone: '', claimantStreet: '', claimantCity: '',
  claimantState: '', claimantZip: '', craft: '', railroad: '', workLocation: '',
  // Bob 2026-10-17: the roster facts are the claimant's, never carried from a previous choice.
  rosterNumber: '', seniorityDate: '', employeeNumber: '', claimantJobSymbol: '' });

// Record 600 / Record 603: what the claim's agreement bars or limits for the chosen category - its own words, with the
// rule and the line they are on. Shown in Box 1 when the category is picked and again at Review & Submit (Record 603),
// so the member meets it at the moment he files. The member can still file. Information, never a block.
function BarWarning({ bars, kindLabel, style }) {
  if (!Array.isArray(bars) || !bars.length) return null;
  return (
    <div style={{ marginTop: 10, background: '#2A1F08', border: '1px solid #E8A020', borderRadius: 6, padding: '10px 12px', ...(style || {}) }}>
      <div style={{ fontSize: 12, color: '#E8A020', fontWeight: 700, marginBottom: 4 }}>⚠ {BAR_HEADING}</div>
      {kindLabel ? <div style={{ fontSize: 12, color: '#F4F6F8', marginBottom: 4 }}>Kind of claim: {kindLabel}</div> : null}
      <div style={{ fontSize: 11.5, color: '#B8C4D0', marginBottom: 8 }}>{BAR_NOTE}</div>
      {bars.map(b => (
        <div key={b.source} style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 12, color: '#F4F6F8', fontWeight: 600 }}>{b.agreementName}, {b.rule}</div>
          <div style={{ fontSize: 12.5, color: '#F4F6F8', fontStyle: 'italic', margin: '2px 0' }}>&ldquo;{b.quote}&rdquo;</div>
          {b.note && <div style={{ fontSize: 11, color: '#B8C4D0' }}>{b.note}</div>}
          <div style={{ fontSize: 10, color: '#8AA4BF' }}>{b.source}</div>
          {b.alsoAt && (
            <div style={{ fontSize: 10, color: '#8AA4BF' }}>Also at: {b.alsoAt.rule} ({b.alsoAt.source}). {b.alsoAt.note || ''}</div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function QuickFile({ user, onNavigate }) {
  const [step, setStep] = useState(0);
  const isOfficer = !!user && (user.isAdmin || OFFICER_ROLES.includes(user.role));
  const basisLabel = memberBasisLabel(user);   // Record 612: '' for anyone but a member
  const [forSelf, setForSelf] = useState(!isOfficer);
  const [division, setDivision] = useState(divisionOfCraft(user?.craft || ''));   // section 807: the division button
  const [claim, setClaim] = useState({
    ...(isOfficer ? blankIdentity() : selfIdentity(user)),
    eventLocation: '',
    description: '',
    incidentDate: '',
    incidentTime12: '',
    incidentTime24: '',
    violatorName: '',
    violatorRosterNumber: '',
    witnesses: '',
  });
  const W = wordsFor(claim.craft || division);   // section 807
  const [matchedRules, setMatchedRules] = useState([]);
  // Bob 2026-10-17 (plan YCP_PLAN_OVERTIME_CLAIM_FACTS_2026-10-17.md, B): an officer filing for a member gets the member's
  // roster number, seniority date, job symbol and employee number from the member's application in his territory - only
  // into fields still empty, every one editable. A query the rules would refuse is never run (claimantRosterFacts.js).
  const [rosterNote, setRosterNote] = useState('');
  const lookupRoster = async (email) => {
    if (forSelf || !isOfficer) return;
    const plan = rosterQueryFor(user, email);
    if (!plan) { setRosterNote(''); return; }
    try {
      const cons = [where('email', '==', plan.email)];
      if (plan.scopeField) cons.push(plan.scopeValues.length === 1 ? where(plan.scopeField, '==', plan.scopeValues[0]) : where(plan.scopeField, 'in', plan.scopeValues));
      let snap = await getDocs(query(collection(db, 'applications'), ...cons));
      // An applicant may have typed his address with capitals; the lower-case form is tried first, then as entered.
      if (snap.empty && String(email).trim() !== plan.email) {
        const cons2 = [where('email', '==', String(email).trim())].concat(cons.slice(1));
        snap = await getDocs(query(collection(db, 'applications'), ...cons2));
      }
      const app = pickApplication(snap.docs.map(d => d.data()));
      const res = factsFromApplication(app, claim);
      if (res) setClaim(prev => {
        const p = {};
        Object.keys(res.patch).forEach(k => { if (!String(prev[k] || '').trim()) p[k] = res.patch[k]; });   // never over what was typed
        return { ...prev, ...p };
      });
      setRosterNote(rosterNotice(res));
    } catch (e) {
      setRosterNote('The application record could not be read (' + ((e && e.message) || 'refused') + '). Enter his roster facts.');
    }
  };
  const [askWork, setAskWork] = useState(false);   // section 536
  // Record 603: the two-step picker's open group. null = follow the chosen category (a resumed draft opens on its
  // group); '' = "Change group" pressed (the six buttons show); a group name = that group is open.
  const [kindGroup, setKindGroup] = useState(null);
  const kindPicker = BOX1_GUIDED ? claimKindPicker(kindGroup, claim.claimKind) : null;
  const [workNote, setWorkNote] = useState('');
  const [newAward, setNewAward] = useState({});
  const [newExhibit, setNewExhibit] = useState('');
  // SECTION 412 PART B: exhibit FILES staged here, uploaded on submit once the claim has an id.
  const [exhibitFiles, setExhibitFiles] = useState([]);

  // DRAFT IDENTITY. Until 2026-08-23 QuickFile had none: handleSaveDraft did addDoc every time, so
  // a member who saved twice ended up with two draft documents and no way to tell them apart, and
  // reopening QuickFile showed him none of them. Worse, handleSubmit also did addDoc, so saving a
  // draft and then filing left the draft behind as an orphan beside the submitted claim.
  //
  // NewClaim already carried draftId and the update-or-create shape. This is the same shape, and
  // the ordering decision lives in claimDrafts.js where it is tested without Firestore.
  const [draftId, setDraftId] = useState(null);
  const [otherDraftCount, setOtherDraftCount] = useState(0);

  // Find the member's most recent draft, and OFFER it - never pour it in unasked (Bob 2026-10-14: "this is supposed
  // to be empty when I get here"; "the date was here, I didnt enter it"). New Claim opens empty; the draft stays saved
  // until he chooses "Continue that draft". The query is equality-only on purpose: adding orderBy('updatedAt') to two
  // where clauses needs a composite index, ycp.cmd deploys indexes only with -Rules, and until that index existed the
  // query would throw at runtime where a silent catch would swallow it. Order is decided in code instead.
  const [offeredDraft, setOfferedDraft] = useState(null);   // { id, data, others }
  useEffect(() => {
    if (!user?.uid || draftId) return;
    getDocs(query(
      collection(db, 'claims'),
      where('createdBy', '==', user.uid),
      where('status', '==', 'draft')
    )).then(snap => {
      const { draft, others } = pickResumableDraft(snap.docs);
      if (!draft) return;
      const data = typeof draft.data === 'function' ? draft.data() : draft;
      setOfferedDraft({ id: draft.id, data, others });
    }).catch(e => { console.error('QuickFile draft lookup failed:', e); });
  }, [user?.uid, draftId]);
  const continueDraft = () => {
    const { id, data, others } = offeredDraft || {};
    if (!id) return;
    const refused = droppedDraftKeys(data);
    if (refused.length) console.debug('QuickFile draft resume: not merged:', refused);
    // Section 510: the saved Box 3 answers go back under their question ids, so the member sees what he wrote.
    setClaim(prev => ({ ...prev, ...draftIntoForm(data), ...box3AnswersToKeys(data.box3Answers) }));
    setDraftId(id);
    setOtherDraftCount(others);
    setOfferedDraft(null);
  };
  const draftSavedOn = (d) => {
    const t = d && d.updatedAt && typeof d.updatedAt.toDate === 'function' ? d.updatedAt.toDate() : null;
    return t ? t.toLocaleDateString() : '';
  };

  // Load user profile from Firestore on mount - pre-fill railroad and workLocation
  useEffect(() => {
    const loadProfile = async () => {
      // Record 635: an officer files for a member here (SECTION 528). The signed-in profile is the OFFICER's, so it is never
      // poured into the claimant fields - that put his own phone, address, craft and railroad on the member's claim.
      // An officer filing for himself chooses "Myself", which fills them from his own identity (selfIdentity).
      if (!user?.uid || OFFICER_ROLES.includes(user?.role)) return;
      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        if (snap.exists()) {
          const data = snap.data();
          setClaim(prev => ({
            ...prev,
            claimantPhone: prev.claimantPhone || data.phone || '',
            claimantStreet: prev.claimantStreet || data.street || '',
            claimantCity: prev.claimantCity || data.city || '',
            claimantState: prev.claimantState || data.state || '',
            claimantZip: prev.claimantZip || data.zip || '',
            // Craft is restored INDEPENDENTLY of railroad. It was missing entirely, while
            // NewClaim's equivalent effect has always restored it - the two intake screens
            // disagreed and this was the one that dropped it.
            craft: prev.craft || data.craft || '',
            // Bob 2026-10-17: his own roster facts, where his profile holds them (memberWritableOnly allows these fields).
            rosterNumber: prev.rosterNumber || data.rosterNumber || '',
            seniorityDate: prev.seniorityDate || data.seniorityDate || '',
            employeeNumber: prev.employeeNumber || data.employeeNumber || '',
            claimantJobSymbol: prev.claimantJobSymbol || data.claimantJobSymbol || '',
            ...(data.railroad && !prev.railroad ? { railroad: data.railroad, workLocation: data.workLocation || prev.workLocation } : {}),
          }));
        }
      } catch (e) {
        console.error('Profile load error:', e);
      }
    };
    loadProfile();
  }, [user?.uid]);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const descRef = useRef();
  // SECTION 540 (Bob: "Spelling and grammar should be automatic and invisible"). When he leaves the description box, his
  // spelling and grammar are corrected in place - no button. A correction is applied only when it changes no name, date
  // or number (the server lists any that differ) and keeps the length within reason; otherwise his words stand as typed.
  // What he typed is kept on the record as descriptionAsWritten (ruling 265: his words stay on the record unchanged).
  const lastProofed = useRef('');
  const autoProofread = async () => {
    const t = String((descRef.current && descRef.current.value) || claim.description || '').trim();
    if (t.length < 20 || t === lastProofed.current) return;
    lastProofed.current = t;
    try {
      const r = await httpsCallable(getFunctions(), 'claimLanguage')({ mode: 'proofread', text: t, facts: {} });
      const out = String((r.data && r.data.text) || '').trim();
      if (!out || out === t || (r.data.unsupported || []).length) return;
      const ratio = out.length / t.length;
      if (ratio < 0.8 || ratio > 1.3) return;
      lastProofed.current = out;
      setClaim(prev => (String(prev.description || '').trim() === t ? { ...prev, description: out, descriptionAsWritten: t } : prev));
    } catch (e) { /* his words stand as typed */ }
  };

  // Box 3 per-question UI state ('open' = typing, 'done' = collapsed). Not saved to the claim.
  const [qaState, setQaState] = useState({});
  const setQa = (id, s) => setQaState(prev => ({ ...prev, [id]: s }));

  const set = (k, v) => setClaim(prev => {
    const updated = { ...prev, [k]: v };
    // input is type="time" -> already 24-hour "HH:MM". Store as HHMM, same as NewClaim.
    if (k === 'incidentTime12') updated.incidentTime24 = v ? v.replace(':', '') : '';
    return updated;
  });

  // Appends spoken/typed text to any claim field (used by the DictationButton).
  const appendTo = (k, t) => setClaim(prev => ({ ...prev, [k]: (prev[k] ? String(prev[k]).trim() + ' ' : '') + t }));

  const generalCommittee = COMMITTEE_LOOKUP[claim.craft]?.[claim.railroad] || '';
  const agreement = AGREEMENT_LOOKUP[generalCommittee]?.[claim.craft]?.[claim.railroad] || '';
  const locations = LOCATIONS_BY_RAILROAD[(claim.craft ? claim.craft + "\u241F" : "") + claim.railroad] || LOCATIONS_BY_RAILROAD[claim.railroad] || [];
  const localCommittee = resolveLocalCommittee(claim.craft, claim.railroad, claim.workLocation);
  // Record 609 (ruling 294): the seats of record, read so the furtherance submission can be recorded at filing.
  const seatsAtFiling = useSeatsOfRecord(generalCommittee);

  // See the note at the requirements panel: matchedRules and the computed amount are visible
  // to the member but are not in `claim`, so they are joined in here for requirement testing only.
  // eventLocation RESOLVES THE SAME WAY EVERY OTHER READER RESOLVES IT. The field initialises to ''
  // and five sites in this file already read it as `eventLocation || workLocation` - the select even
  // DISPLAYS that fallback while only writing eventLocation when the member actively changes it. So
  // a member who accepts the default has '' in the bag, and a requirement keyed on eventLocation
  // alone would show "Occurrence (date, location)" unmet while the location is on screen above it.
  // That was half a fix; this is the other half.
  const requirementsView = { ...claim, matchedRules,
    eventLocation: claim.eventLocation || claim.workLocation || '',
    amountClaimed: computeAmountClaimed(claim) || '' };


  const analyzeDescription = async () => {
    // Three silent failures lived here. The screen promises that applicable rules identify
    // automatically, and every one of these paths broke that promise without a word:
    // a missing agreement returned early, a thrown error went to console.error where no
    // member or officer will ever see it, and an empty result set did nothing at all.
    // A claim path may fail. It may not fail silently.
    if (!claim.description?.trim()) return;
    if (!agreement) {
      setAnalysisError('Rule analysis needs the governing agreement, and none is set yet. Craft, railroad and work location are on Step 1 - go Back to set them and the agreement resolves from there. Nothing you have typed is lost.');
      return;
    }
    setAnalyzing(true);
    setAnalysisError('');
    try {
      const res = await fetch(CLOUD_FUNCTION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + (await auth.currentUser?.getIdToken() || '') },
        body: JSON.stringify({
          agreement,
          description: claim.description,
          // Ruling 261: the kind of claim narrows the rules offered to the analysis. Off, or no kind: the full list.
          availableRules: (BOX1_GUIDED && claim.claimKind) ? narrowRules(claim.claimKind, AGREEMENT_RULES[agreement] || []) : (AGREEMENT_RULES[agreement] || [])
        })
      });
      if (!res.ok) throw new Error('rule analysis returned ' + res.status);
      const data = await res.json();
      if (Array.isArray(data.rules) && data.rules.length > 0) {
        setMatchedRules(agreementFamily(agreement) === 'amtrak-ym' ? canonicalizeAmtrakRules(data.rules) : sanitizeMatchedRules(data.rules, agreement));
      } else if (data.held === true) {
        // Ruling 90: the agreement is withheld from analysis; the function says why. Not a no-match.
        setAnalysisError(data.heldReason || 'Rule analysis for this agreement is withheld until its rules have been verified. Choose the rule yourself from the rulebook below.');
      } else {
        setAnalysisError('No matching rules identified from that description. Add more detail, or choose the rule yourself from the rulebook below.');
      }
    } catch (e) {
      console.error('Analysis failed', e);
      setAnalysisError('Rule analysis is unavailable right now. Nothing is lost - choose the rule yourself from the rulebook below, and the claim files normally.');
    } finally {
      setAnalyzing(false);
    }
  };

  // Ruling 261: choosing (or changing) the kind of claim re-runs the rule analysis on what is already written,
  // so the narrowed rule list takes effect at once. Nothing runs while the switch is off or no kind is chosen.
  useEffect(() => {
    if (BOX1_GUIDED && claim.claimKind && claim.description?.trim()) analyzeDescription();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claim.claimKind]);

  // QuickFile keeps the agreement, the committees and the claim fields in separate pieces of state.
  // The builder takes ONE bag, so they are joined here, once, instead of at each write site. Note
  // that localCommittee is NOT passed: the builder derives it from the scopeKey, which is the
  // authority (routing.js says so in its own comments), so QuickFile's copy can no longer disagree.
  const quickFileForm = () => ({
    ...claim,
    eventLocation: claim.eventLocation || claim.workLocation,
    generalCommittee,
    applicableAgreement: agreement,
    // Section 510: every answered Box 3 question, saved with the question as shown. Always written from Quick File
    // (an empty list when none is answered), so clearing an answer on a draft clears it on the record too.
    box3Answers: collectBox3Answers(claim, allBox3Questions(matchedRules, claim.craft)),
  });

  const handleSaveDraft = async () => {
    setSaving(true);
    setError('');
    try {
      // QuickFile does not ask the member to confirm his profile, so profileConfirmed is FALSE here
      // as a statement of fact rather than as a hardcoded convenience. It is passed explicitly for
      // that reason.
      // UPDATE THE DRAFT IF THERE IS ONE. Saving twice used to create two documents.
      const mk = (isNew) => buildClaimPayload({
        form: quickFileForm(),
        uid: user?.uid || auth.currentUser?.uid || '', email: user?.email || '',
        profileConfirmed: false, matchedRules, approvedNarrative: '',
        mode: 'draft', serverTimestamp: serverTimestamp(), isNew,
      });
      if (draftId) {
        await updateDoc(doc(db, 'claims', draftId), claimPatch(mk(false), user || auth.currentUser));
      } else {
        const ref = await addDoc(collection(db, 'claims'), claimPatch(mk(true), user || auth.currentUser));
        setDraftId(ref.id);
      }
      // R-5: only when the filer is the claimant.
      if (user?.uid && filerIsClaimant({ filerEmail: user?.email, claimantEmail: claim.claimantEmail })) {
        await setDoc(doc(db, 'users', user.uid), profilePatch({
          craft: claim.craft,
          railroad: claim.railroad,
          workLocation: claim.workLocation,
          name: claim.claimantName,
          email: claim.claimantEmail,
          phone: claim.claimantPhone,
        }), { merge: true });
      }
      onNavigate('claims');
    } catch (e) {
      setError('Failed to save draft: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    // Bob 2026-10-14 (the walk-through): refuse an e-mail or phone the platform cannot use, in plain words, before filing.
    const contactBad = emailProblem(claim.claimantEmail) || phoneProblem(claim.claimantPhone);
    if (contactBad) { setError('Not filed yet: ' + contactBad + ' Fix it on step 1.'); return; }
    setSaving(true);
    setError('');
    try {
      const claimNum = await nextClaimNumber(generalCommittee);
      // FILE THE DRAFT IN PLACE rather than beside it. This used to addDoc unconditionally, so a
      // member who saved a draft and then filed ended up with BOTH - an orphan draft carrying the
      // same claim, and no link between them. Promoting the draft document to `submitted` is still
      // ONE write, which is what defect D-9 was about.
      const mkS = (isNew) => buildClaimPayload({
        form: quickFileForm(),
        uid: user?.uid || auth.currentUser?.uid || '', email: user?.email || '',
        profileConfirmed: false, matchedRules, approvedNarrative: '',
        mode: 'submit', claimNumber: claimNum,
        serverTimestamp: serverTimestamp(), isNew,
      });
      let claimIdForDocs;
      // Ruling 271: the record as written, held so the filer's copy is drawn from exactly what was filed.
      const filedPayload = mkS(!draftId);
      if (draftId) {
        await updateDoc(doc(db, 'claims', draftId), claimPatch(mkS(false), user || auth.currentUser));
        claimIdForDocs = draftId;
        setDraftId(null);
      } else {
        const ref = await addDoc(collection(db, 'claims'), claimPatch(mkS(true), user || auth.currentUser));
        claimIdForDocs = ref.id;
      }
      // Record 609 (ruling 294): filed - the furtherance submission to the Local Chairperson of record is recorded by
      // default, once. It never blocks the filing: a refusal comes back as a line, shown before the screen moves on.
      const carried = await recordCarriageAtFiling({ claimId: claimIdForDocs, claim: filedPayload,
        filerUid: user?.uid || auth.currentUser?.uid || '', roster: seatsAtFiling.roster, rosterSource: seatsAtFiling.source });

      // SECTION 412 PART B: attach the exhibit FILES he staged, now that the claim has an id. Each goes
      // through the claimDocumentUploadUrl callable (secure by construction). A per-file failure - or a
      // claim not yet routed to a Local, which the callable refuses to scope - does NOT lose the claim
      // or the other files; it is reported so he can attach it later from the claim's Documents panel.
      if (exhibitFiles.length && claimIdForDocs) {
        const { entries, errors } = await attachStagedExhibits({ claimId: claimIdForDocs, exhibitFiles, user });
        if (entries.length) {
          await updateDoc(doc(db, 'claims', claimIdForDocs), claimPatch({ documents: arrayUnion(...entries), updatedAt: serverTimestamp() }, user || auth.currentUser));
        }
        if (errors.length) {
          alert('Your claim was filed. ' + entries.length + ' of ' + exhibitFiles.length + ' exhibit file(s) attached. These could not be attached and can be added from the claim\'s Documents panel:\n\n' + errors.join('\n'));
        }
      }

      if (!resolveScopeKey(claim.craft, claim.railroad, claim.workLocation)) {
        // CORRECTED 2026-09-22 (section 364, audit finding D5). This used to say 'your filing deadline
        // is protected'. THE PLATFORM CANNOT KNOW THAT. Whether a filing was timely is decided under the
        // agreement by the officers who hold it, and this line was fired on the path where routing had
        // just FAILED - so the platform was promising a legal consequence at the moment it knew least.
        // It now states what happened and nothing else.
        alert('Your claim has been filed. Your work location is not yet set up in the system, so it has been routed to your General Chairperson to assign to the correct Local Chairperson. Nothing on this platform decides whether a filing was on time - that is decided under your agreement.');
      }
      // Save profile fields for future pre-fill - R-5: only when the filer is the claimant.
      if (user?.uid && filerIsClaimant({ filerEmail: user?.email, claimantEmail: claim.claimantEmail })) {
        await setDoc(doc(db, 'users', user.uid), profilePatch({
          craft: claim.craft,
          railroad: claim.railroad,
          workLocation: claim.workLocation,
          phone: claim.claimantPhone,
          name: claim.claimantName,
          email: claim.claimantEmail,
        }), { merge: true });
      }
      // RULING 271 (2026-10-02): on submit, the filer is given the claim as filed - every page - to keep, before the
      // screen moves on. The platform's record is untouched. A copy that cannot be drawn never blocks the filing.
      if (RULING_271_FILER_COPY) {
        const copy = await filerCopy({ ...filedPayload, id: claimNum }, user);
        if (copy.ok && saveCopy(copy)) setTimeout(() => URL.revokeObjectURL(copy.url), 60000);
        else {
          alert('Your claim was filed. Your copy could not be saved here' + (copy.error ? ' (' + copy.error + ')' : '') + ' - download it from the claim page.');
        }
      }
      if (carried.line) alert(carried.line);   // Record 609 (ruling 294)
      onNavigate('claims');
    } catch (e) {
      setError('Failed to file claim: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const canProceed = () => {
    // CRAFT IS GATED HERE. It was not, and that is how a member reached step 4 with no craft:
    // the profile prefill below restores railroad and workLocation but never restored craft, so
    // the three gated fields could all be present while the one the agreement actually keys on
    // was empty. The control is marked "Craft *" on this very step; the gate simply did not read it.
    if (step === 0) return claim.claimantName && claim.craft && claim.railroad && claim.workLocation;
    if (step === 1) return claim.incidentDate;
    if (step === 2) return true;
    if (step === 3) return claim.description?.trim().length > 10;
    return true;
  };

  return (
    <div>
      <div className="topbar">
        <div>
          <h1 className="topbar-title">New Claim</h1>
          <div className="topbar-meta">Guided claim builder - 5 steps (need the old form? use Full Form)</div>
        </div>
        <div className="topbar-actions">
          <button className="btn btn-secondary" onClick={() => onNavigate('full-form')}>Full Form</button>
          <button className="btn btn-secondary" onClick={() => onNavigate('claims')}>Cancel</button>
        </div>
      </div>

      <div className="page-content" style={{ maxWidth: 640, margin: '0 auto' }}>

        {/* Progress bar */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 24 }}>
          {STEPS.map((s, i) => (
            <div key={s.id} style={{ flex: 1, height: 4, borderRadius: 2,
              background: i <= step ? '#E8A020' : '#2E4A6B', transition: 'background 0.3s' }} />
          ))}
        </div>
        <div style={{ fontSize: 13, color: '#E8A020', fontWeight: 700, marginBottom: 20 }}>
          Step {step + 1} of {STEPS.length} — {STEPS[step].label}
        </div>

        {offeredDraft && !draftId && (
          <div style={{ background: '#13263D', border: '1px solid #2E4A6B', borderRadius: 4,
                        padding: '8px 12px', marginBottom: 12, fontSize: 13, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span>You have a claim you started and saved{draftSavedOn(offeredDraft.data) ? ' on ' + draftSavedOn(offeredDraft.data) : ''}. This form is empty - it is a new claim.</span>
            <button type="button" className="btn btn-secondary btn-sm" onClick={continueDraft}>Continue that draft instead</button>
          </div>
        )}

        {/* A member with more than one draft is TOLD so. Silently binding the newest is still
            silent, and silence is what the original defect was. */}
        {otherDraftCount > 0 && (
          <div style={{ background: '#2A2A2A', border: '1px solid #555', borderRadius: 4,
                        padding: '8px 12px', marginBottom: 12, fontSize: 13 }}>
            {multipleDraftsNotice(otherDraftCount)}
          </div>
        )}

        {/* STEP 0 — Who are you */}
        {step === 0 && (
          <div>
            <div className="card">
              <div className="card-header"><div className="card-title">{forSelf ? 'Your Information' : "The Claimant's Information"}</div></div>
              {isOfficer ? (
                <div className="form-group" style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', fontSize: 13 }}>
                  <span style={{ fontWeight: 600 }}>Who is this claim for?</span>
                  <label style={{ cursor: 'pointer' }}><input aria-label="A member" type="radio" checked={!forSelf} onChange={() => { setForSelf(false); setClaim(c => ({ ...c, ...blankIdentity() })); }} /> A member</label>
                  <label style={{ cursor: 'pointer' }}><input aria-label="This claim is for myself" type="radio" checked={forSelf} onChange={() => { setForSelf(true); setClaim(c => ({ ...c, ...selfIdentity(user) })); }} /> Myself</label>
                </div>
              ) : null}
              <div className="form-group">
                <label className="form-label">{forSelf ? 'Your Name *' : "Claimant's Name *"}</label>
                <input aria-label="Full name" className="form-input" style={{ fontSize: 15, fontWeight: 600 }} placeholder="Full name" value={claim.claimantName} onChange={e => set('claimantName', e.target.value)} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Cell Phone</label>
                  <input aria-label="Cell Phone" className="form-input" placeholder="(555) 555-5555" value={claim.claimantPhone} onChange={e => set('claimantPhone', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input aria-label="Email" className="form-input" placeholder="your@email.com" value={claim.claimantEmail} onChange={e => set('claimantEmail', e.target.value)}
                    onBlur={e => lookupRoster(e.target.value)} />
                </div>
              </div>
              {!forSelf && isOfficer && rosterNote && (
                <div style={{ fontSize: 12, color: '#B8C4D0', margin: '-4px 0 10px' }}>{rosterNote}</div>
              )}
              {/* Bob 2026-10-17: the claimant's roster facts - the carrier denies a claim that does not show where he stands.
                  Filled from his application when an officer files for him; always editable. */}
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Roster Number</label>
                  <input aria-label="Roster Number" className="form-input" placeholder="e.g. 42" value={claim.rosterNumber || ''} onChange={e => set('rosterNumber', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Seniority Date</label>
                  <input aria-label="Seniority Date" className="form-input" type="date" value={claim.seniorityDate || ''} onChange={e => set('seniorityDate', e.target.value)} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Employee Number</label>
                  <input aria-label="Employee Number" className="form-input" placeholder="Carrier ID" value={claim.employeeNumber || ''} onChange={e => set('employeeNumber', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Job Symbol (his current assignment)</label>
                  <input aria-label="Job Symbol (his current assignment)" className="form-input" placeholder="e.g. YM-3" value={claim.claimantJobSymbol || ''} onChange={e => set('claimantJobSymbol', e.target.value)} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Mailing Address</label>
                <input aria-label="Mailing Address" className="form-input" placeholder="Street address" value={claim.claimantStreet} onChange={e => set('claimantStreet', e.target.value)} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">City</label>
                  <input aria-label="City" className="form-input" placeholder="City" value={claim.claimantCity} onChange={e => set('claimantCity', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">State</label>
                  <input aria-label="State" className="form-input" placeholder="ST" value={claim.claimantState} onChange={e => set('claimantState', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">ZIP</label>
                  <input aria-label="ZIP" className="form-input" placeholder="ZIP" value={claim.claimantZip} onChange={e => set('claimantZip', e.target.value)} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Craft *</label>
                <DivisionButtons value={division} onPick={(d) => { setDivision(d); set('craft', ''); set('railroad', ''); set('workLocation', ''); }} />
                <select aria-label="Craft" className="form-select" value={claim.craft} disabled={DIVISIONS.length > 1 && !division} onChange={e => { set('craft', e.target.value); set('railroad', ''); set('workLocation', ''); }}>
                  <option value="">{DIVISIONS.length > 1 && !division ? 'Choose your division first' : 'Select your craft...'}</option>
                  {craftsInDivision(division, Object.keys(RAILROADS)).map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Railroad *</label>
                <select aria-label="Railroad" className="form-select" value={claim.railroad} disabled={!claim.craft} onChange={e => { set('railroad', e.target.value); set('workLocation', ''); }}>
                  <option value="">{claim.craft ? 'Select your railroad...' : 'Select craft first'}</option>
                  {claim.craft && RAILROADS[claim.craft].map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              {claim.railroad && (
                <div className="form-group">
                  <label className="form-label">Your Work Location *</label>
                  <select aria-label="Your Work Location" className="form-select" value={claim.workLocation} onChange={e => set('workLocation', e.target.value)}>
                    <option value="">Select your assigned desk...</option>
                    {locations.map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
              )}
              {claim.railroad && (
                <div className="form-group">
                  <label className="form-label">Your Job Symbol / Assignment</label>
                  <input aria-label="Your Job Symbol / Assignment" className="form-input" placeholder="e.g. 0600-1400, Y090 PSCC" value={claim.claimantJobSymbol || ''} onChange={e => set('claimantJobSymbol', e.target.value)} />
                  <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontWeight: 400 }}>
                    <input aria-label="Hours of service were a factor for the claimant" type="checkbox" style={{ width: 'auto' }} checked={!!claim.hosFactorClaimant} onChange={e => set('hosFactorClaimant', e.target.checked)} />
                    Hours of Service note — for the record (FRA fact-finding, not a claim factor): rest, hours already on duty
                  </label>
                  {claim.hosFactorClaimant && (
                    <textarea aria-label="Hours already on duty this tour/week, rest status, or staffing detail." className="form-input" rows={2} style={{ resize: 'vertical', marginTop: 6 }} placeholder="Hours already on duty this tour/week, rest status, or staffing detail." value={claim.hosNoteClaimant || ''} onChange={e => set('hosNoteClaimant', e.target.value)} />
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* STEP 3 — What happened — 5 boxes + comments */}
        {step === 3 && (
          <div>
            {/* Bob 2026-10-17 ("can you fix it without me"): the page reads top to bottom in the order the claim is made -
                what happened, the rule, the statement from both, exhibits, who receives it - and what is still needed is
                listed last, just above Continue, each open item naming where it is answered. Plan C,
                data/YCP_PLAN_OVERTIME_CLAIM_FACTS_2026-10-17.md. */}
            <div style={{ fontSize: 12, color: '#8AA4BF', marginBottom: 10 }}>This page's one job: put a complete claim on the record. Work down the page: what happened, the rule, the statement, exhibits, then who receives it. Board awards are under More - you do not need any to file.</div>   {/* ruling 276, screen 2 */}

            {/* The panel is handed a VIEW of the bag, not the bag: matchedRules and the computed
              amount are on screen but live outside form state, so a requirement keyed on either
              could never tick no matter what the member did. amountClaimed is blanked when zero -
              String(0) is truthy and a zero remedy is not a stated remedy. */}
        <EligibilityPayablePanel part="deadline" agreement={agreement} form={requirementsView} /><HowToPanel kind="claim" agreement={agreement} defaultOpen={!(user && (user.role === 'local_chair' || user.role === 'general_chair' || user.isAdmin))} />{/* Bob 2026-10-15: the step-by-step, per agreement; 2026-10-17: only the deadline up here, the checklist above Continue */}

            {/* BOX 1 — What Happened */}
            <div className="card" style={{ marginBottom: 14 }}>
              <div className="card-header">
                <div className="card-title">Box 1 — Describe What Happened</div>
                <DictationButton onText={t => appendTo('description', t)} />
              </div>
              {BOX1_GUIDED && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 12, color: '#F4F6F8', fontWeight: 600, marginBottom: 6 }}>What kind of claim is this?</div>
                  {/* Record 600 (claim categories, Bob 10/7): 29 categories under six headings; chips wrap. With
                    CLAIM_CATEGORIES_600 false this is one untitled row of the seven 261 kinds - the 261 screen. */}
                  {/* Record 603 (Bob 10/8): with the 29 on, TWO STEPS - the six group buttons first, then only the chosen
                    group's categories, with "Change group" to go back. A resumed draft opens on its category's group
                    (claimKindPicker, box1Guided.js). With CLAIM_CATEGORIES_600 false the 261 row below is unchanged. */}
                  {kindPicker && kindPicker.twoStep ? (
                    !kindPicker.open ? (
                      <div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {kindPicker.groups.map(gName => (
                            <button type="button" key={gName}
                              className={groupOfKind(claim.claimKind) === gName ? 'btn btn-primary' : 'btn btn-secondary'}
                              style={{ fontSize: 12, whiteSpace: 'normal', textAlign: 'left' }}
                              onClick={() => setKindGroup(gName)}>
                              {gName}
                            </button>
                          ))}
                        </div>
                        {claim.claimKind && claimKind(claim.claimKind) && (
                          <div style={{ fontSize: 11, color: '#8AA4BF', marginTop: 6 }}>
                            Chosen: {claimKind(claim.claimKind).label}. Open a group to choose a different kind.
                          </div>
                        )}
                      </div>
                    ) : (
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                          <div style={{ fontSize: 10, color: '#8AA4BF', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>{kindPicker.open}</div>
                          <button type="button" className="btn btn-secondary" style={{ fontSize: 11, padding: '2px 8px' }}
                            onClick={() => setKindGroup('')}>
                            Change group
                          </button>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {kindPicker.kinds.map(k => (
                            <button type="button" key={k.id}
                              className={claim.claimKind === k.id ? 'btn btn-primary' : 'btn btn-secondary'}
                              style={{ fontSize: 12, whiteSpace: 'normal', textAlign: 'left' }}
                              onClick={() => set('claimKind', k.id)}>
                              {k.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )
                  ) : claimKindGroups().map(g => (
                    <div key={g.heading || 'kinds'} style={{ marginBottom: g.heading ? 8 : 0 }}>
                      {g.heading && (
                        <div style={{ fontSize: 10, color: '#8AA4BF', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 4 }}>{g.heading}</div>
                      )}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {g.kinds.map(k => (
                          <button type="button" key={k.id}
                            className={claim.claimKind === k.id ? 'btn btn-primary' : 'btn btn-secondary'}
                            style={{ fontSize: 12, whiteSpace: 'normal', textAlign: 'left' }}
                            onClick={() => set('claimKind', k.id)}>
                            {k.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                  {/* Record 600: the claim's agreement bars or limits this category - its own words, with the rule and
                    the line they are on. Shown before filing; the member can still file. Information, never a block. */}
                  {claim.claimKind && <BarWarning bars={barsFor(agreement, claim.claimKind)} />}   {/* Record 603: one component, also at Review & Submit */}
                  {claim.claimKind && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
                      {guidedQuestions(claim.claimKind).map(({ id, q }) => {
                        const answers = claim.box1Answers || {};
                        const found = id === 'q_junior' && claim.violatorName
                          ? claim.violatorName + (claim.violatorRosterNumber ? ' (roster ' + claim.violatorRosterNumber + ')' : '')
                          : id === 'q_datetime' && claim.incidentDate ? claim.incidentDate + (claim.incidentTime24 ? ' at ' + claim.incidentTime24 : '')
                          // Bob 2026-10-17: the claimant's own roster facts (from his application, or typed) - never the filer's.
                          : id === 'q_seniority' && (claim.seniorityDate || claim.rosterNumber)
                            ? [claim.seniorityDate ? 'Seniority date ' + claim.seniorityDate : '', claim.rosterNumber ? 'roster number ' + claim.rosterNumber : ''].filter(Boolean).join(', ')
                          : '';
                        const setA = v => set('box1Answers', { ...(claim.box1Answers || {}), [id]: v });
                        return (
                          <div key={id} style={{ background: '#0A1628', border: `1px solid ${(answers[id] || '').trim() ? '#2E6B4A' : '#2E4A6B'}`, borderRadius: 6, padding: '10px 12px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
                              <div style={{ fontSize: 13, color: '#F4F6F8' }}>{q}</div>
                              <DictationButton compact onText={x => setA(((answers[id] || '').trim() ? answers[id].trim() + ' ' : '') + x)} />
                            </div>
                            {!(answers[id] || '').trim() && found && (
                              <button type="button" className="btn btn-secondary" style={{ fontSize: 11, marginBottom: 6 }} onClick={() => setA(found)}>Use what you entered: {found}</button>
                            )}
                            {!(answers[id] || '').trim() && guidedSuggestion(id) && (
                              <div style={{ background: '#0D1B2A', border: '1px dashed #2E6B4A', borderRadius: 6, padding: '6px 10px', marginBottom: 6 }}>
                                <div style={{ fontSize: 12, color: '#B8C4D0', fontStyle: 'italic', marginBottom: 6 }}>{guidedSuggestion(id)}</div>
                                <button type="button" className="btn btn-secondary" style={{ fontSize: 11 }} onClick={() => setA(guidedSuggestion(id))}>Use this answer</button>
                              </div>
                            )}
                            <textarea aria-label="Your answer (optional)" className="form-textarea" rows={2} placeholder="Your answer (optional)"
                              value={answers[id] || ''} onChange={e => setA(e.target.value)} />
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: '#F4F6F8', fontWeight: 600, margin: '12px 0 4px' }}>Anything else, in your own words</div>
                </div>
              )}
              <div style={{ fontSize: 11, color: '#8AA4BF', marginBottom: 8 }}>
                Describe, in your own words, what happened — the more you tell here, the fewer questions you'll need to answer below. Type it, or tap <b>Talk to type</b> and just say it. Applicable rules identify automatically when you finish.
              </div>
              <textarea aria-label="What happened, in your own words"
                ref={descRef}
                className="form-textarea"
                rows={5}
                placeholder={W.quickFileDescriptionExample}
                value={claim.description}
                onChange={e => set('description', e.target.value)}
                onBlur={() => { analyzeDescription(); autoProofread(); }}
                spellCheck
                autoFocus
              />
              {/* RULING 265: say it your way - then check it, or have it put in claim language. His words stay his. */}
              <ClaimLanguageHelper
                modes={['statement']}
                text={claim.description}
                facts={factsFromClaim({ ...claim, applicableAgreement: agreement, matchedRules })}
                acceptLabel={(mode) => mode === 'proofread' ? 'Use the corrected wording' : 'Use this as my Statement of Claim'}
                onUse={(text, mode) => {
                  if (mode === 'proofread') { set('description', text); return; }
                  setClaim(prev => ({ ...prev, statementOfClaim: { text, approvedBy: user?.uid || auth.currentUser?.uid || '', approvedByName: user?.name || '', approvedAt: new Date().toISOString(), draftedFrom: 'language assistant from the claimant\u2019s own words', edited: true } }));
                }}
              />
              {claim.statementOfClaim && claim.statementOfClaim.approvedBy && !BOX1_GUIDED ? (
                <div style={{ marginTop: 8, background: '#0A1628', border: '1px solid #2E6B4A', borderRadius: 6, padding: '10px 12px' }}>
                  <div style={{ fontSize: 10, color: '#40C070', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 4 }}>Statement of Claim - approved. This is what prints on the claim; your own words above stay on the record.</div>
                  <div style={{ fontSize: 13, color: '#F4F6F8', whiteSpace: 'pre-wrap' }}>{claim.statementOfClaim.text}</div>
                  <button type="button" className="btn btn-secondary btn-sm" style={{ marginTop: 6 }} onClick={() => setClaim(prev => { const n = { ...prev }; delete n.statementOfClaim; return n; })}>Remove it - print my own words</button>
                </div>
              ) : null}
              {analyzing && (
                <div style={{ color: '#8AA4BF', fontSize: 12, marginTop: 8 }}>⚙ Identifying applicable rules from {agreement}...</div>
              )}
              {analysisError && !analyzing && (
                <div style={{ padding: '8px 12px', background: '#2D0A0A', border: '1px solid #C0392B', borderRadius: 6, marginTop: 8, color: '#FF6B6B', fontSize: 12 }}>⚠ {analysisError}</div>
              )}
              {BOX1_GUIDED && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 11.5, color: '#E8A020', marginBottom: 8 }}>{FACTS_LINE}</div>
                  <div style={{ fontSize: 10, color: '#8AA4BF', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 4 }}>What's still missing</div>
                  {missingChecklist(claim, claim.claimKind).map(m => (
                    <div key={m.id} style={{ fontSize: 12, color: m.ok ? '#40C070' : '#C08A2E' }}>{m.ok ? '✓' : '○'} {m.label}</div>
                  ))}
                </div>
              )}
            </div>

            {/* BOX 2 — Rule / basis of claim (catalogued agreements: real picker; else: identified rules) */}
            {hasRuleCatalog(agreement) ? (
              <div className="card" style={{ marginBottom: 14 }}>
                <div className="card-header">
                  <div className="card-title">Box 2 — Rule / basis of claim</div>
                  <div style={{ fontSize: 11, color: '#8AA4BF' }}>{claim.railroad}</div>
                </div>
                <RuleBasisPicker
                  agreement={agreement}
                  location={claim.eventLocation || claim.workLocation}
                  asOfDate={claim.incidentDate}
                  matchedRules={matchedRules}
                  onChange={setMatchedRules}
                  analyzing={analyzing}
                  memberLabel={basisLabel}
                />
              </div>
            ) : (matchedRules.length > 0 && !analyzing && (
              <div className="card" style={{ marginBottom: 14 }}>
                <div className="card-header">
                  <div className="card-title">Box 2 — Possible Basis</div>
                  <div style={{ fontSize: 11, color: '#8AA4BF' }}>{claim.railroad}</div>
                </div>
                <div style={{ fontSize: 11, color: '#8AB4E8', background: '#0D1B2A', border: '1px solid #2E4A6B', borderRadius: 6, padding: '7px 10px', marginBottom: 10 }}>
                  AI-suggested starting points from your description. This agreement isn't catalogued yet, so the governing rule text will be confirmed by your Local / General Chair — nothing here is filed as agreement language.
                </div>
                {basisLabel ? <div style={{ fontSize: 11, color: '#FFD27A', margin: '0 0 8px' }}>{basisLabel}</div> : null}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  {matchedRules.map((rule, i) => (
                    <div key={i} style={{ background: '#0A1628', border: '1px solid #2E4A6B', borderRadius: 6, padding: '10px 12px' }}>
                      <div style={{ fontSize: 13, color: '#E8A020', fontWeight: 700, marginBottom: 3 }}>{rule.rule}</div>
                      {rule.exactLanguage && <div style={{ fontSize: 11, color: '#F4F6F8', fontStyle: 'normal', borderLeft: '3px solid #E8A020', paddingLeft: 6, marginBottom: 4 }}>"{rule.exactLanguage}"</div>}
                      <div style={{ fontSize: 11, color: '#40C070' }}>✓ {rule.relevance}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {BOX1_GUIDED && (() => {   /* Bob 2026-10-17, plan C: the statement follows the rule it is drafted from */
                    // RULING 258: the Statement of Claim, drafted in the agreement's own words from her entries, shown
                    // beside her own words. It never prints as final until she (or the officer filing for her) approves it.
                    const d = draftStatementOfClaim({ ...quickFileForm(), matchedRules });
                    const approved = claim.statementOfClaim && claim.statementOfClaim.approvedBy ? claim.statementOfClaim : null;
                    if (!d.drafted && !approved) return null;
                    const editing = qaState.__statement === 'edit';
                    return (
                      <div className="card" style={{ marginBottom: 14 }}>
                      <div className="card-header"><div className="card-title">Statement of Claim</div><div style={{ fontSize: 11, color: '#8AA4BF' }}>drafted from what happened and the rule</div></div>
                      <div style={{ marginTop: 4, background: '#0A1628', border: `1px solid ${approved ? '#2E6B4A' : '#2E4A6B'}`, borderRadius: 6, padding: '10px 12px' }}>
                        <div style={{ fontSize: 10, color: '#40C070', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 4 }}>
                          Statement of Claim {approved ? '— approved' : '— drafted from your answers and ' + d.rule + ' (not yet approved)'}
                        </div>
                        {editing ? (
                          <textarea aria-label="Statement edit" className="form-textarea" rows={5} value={claim.__statementEdit ?? (approved ? approved.text : d.text)}
                            onChange={e => set('__statementEdit', e.target.value)} />
                        ) : (
                          <div style={{ fontSize: 13, color: '#F4F6F8', whiteSpace: 'pre-wrap' }}>{approved ? approved.text : d.text}</div>
                        )}
                        {approved && d.drafted && approved.text !== d.text && !editing && (
                          <div style={{ fontSize: 11, color: '#C08A2E', marginTop: 6 }}>Your answers have changed since you approved this. The draft now reads differently - review it again if you wish.</div>
                        )}
                        <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                          <button type="button" className="btn btn-primary" style={{ fontSize: 11, background: '#27AE60' }}
                            onClick={() => {
                              const text = editing ? String(claim.__statementEdit ?? (approved ? approved.text : d.text)).trim() : d.text;
                              if (!text) return;
                              setClaim(prev => { const n = { ...prev, statementOfClaim: { text, approvedBy: user?.uid || auth.currentUser?.uid || '', approvedByName: user?.name || '', approvedAt: new Date().toISOString(), draftedFrom: d.rule, edited: editing && text !== d.text } }; delete n.__statementEdit; return n; });
                              setQa('__statement', 'done');
                            }}>
                            {editing ? 'Approve my edited statement' : (approved && d.drafted && approved.text !== d.text ? 'Approve the new draft' : (approved ? 'Approved' : 'Approve this statement'))}
                          </button>
                          {!editing && <button type="button" className="btn btn-secondary" style={{ fontSize: 11 }} onClick={() => setQa('__statement', 'edit')}>Edit</button>}
                          {editing && <button type="button" className="btn btn-secondary" style={{ fontSize: 11 }} onClick={() => { setClaim(prev => { const n = { ...prev }; delete n.__statementEdit; return n; }); setQa('__statement', 'done'); }}>Cancel</button>}
                        </div>
                        <div style={{ fontSize: 11, color: '#8AA4BF', marginTop: 6 }}>Your own words above stay on your claim exactly as you wrote them.</div>
                      </div>
                      </div>
                    );
            })()}

            {/* BOX 3 — Guidance to Claimant. Ruling 261: in the guided Box 1 these questions are asked there instead. */}
            {!BOX1_GUIDED && matchedRules.length > 0 && (
              <div className="card" style={{ marginBottom: 14 }}>
                <div className="card-header">
                  <div className="card-title">Box 3 — Complete Your Claim</div>
                  {/* Section 364 (D6): "these strengthen the claim" assessed his claim on the filing
                      path, which ruling 204 forecloses. */}
                  <div style={{ fontSize: 11, color: '#8AA4BF' }}>Answer what you can — none are required to file. Your Local or General Chair decides what is actually needed.</div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  {getQuestionSet(matchedRules, claim.craft).map(({ id, q, type }) => {
                    const inputStyle = { width: '100%', background: '#0D1B2A', border: '1px solid #2E4A6B', borderRadius: 4, padding: '10px 12px', color: '#F4F6F8', fontSize: 15, boxSizing: 'border-box' };
                    const subLabel = { fontSize: 11, color: '#8AA4BF', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 4 };

                    if (type === 'datetime') {
                      const answered = (claim[`${id}_date`] || claim[`${id}_time`]);
                      return (
                        <div key={id} style={{ gridColumn: '1 / -1', background: '#0A1628', border: `1px solid ${answered ? '#2E6B4A' : '#2E4A6B'}`, borderRadius: 6, padding: '14px 16px' }}>
                          <div style={{ fontSize: 14, color: '#F4F6F8', marginBottom: 10 }}>{q}</div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <div>
                              <div style={subLabel}>Date</div>
                              <input aria-label="Date" style={inputStyle} type="date" min="1975-01-01" max={TODAY_DATE} value={claim[`${id}_date`] || ''} onChange={e => set(`${id}_date`, e.target.value)} />
                            </div>
                            <div>
                              <div style={subLabel}>Time</div>
                              <input aria-label="Time" style={inputStyle} type="time" value={claim[`${id}_time`] || ''} onChange={e => set(`${id}_time`, e.target.value)} />
                            </div>
                          </div>
                        </div>
                      );
                    }

                    if (type === 'person') {
                      const fields = [
                        { key: 'name', label: 'Full Name' },
                        { key: 'roster', label: 'Roster #' },
                        { key: 'seniority', label: 'Seniority Date', date: true },
                        { key: 'jobsymbol', label: 'Job Symbol #' },
                        { key: 'hours', label: 'Hours of Assignment' },
                        { key: 'restdays', label: 'Rest Days' },
                      ];
                      const answered = fields.some(fl => (claim[`${id}_${fl.key}`] || '').trim());
                      return (
                        <div key={id} style={{ gridColumn: '1 / -1', background: '#0A1628', border: `1px solid ${answered ? '#2E6B4A' : '#2E4A6B'}`, borderRadius: 6, padding: '14px 16px' }}>
                          <div style={{ fontSize: 14, color: '#F4F6F8', marginBottom: 10 }}>{q}</div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                            {fields.map(fl => (
                              <div key={fl.key}>
                                <div style={subLabel}>{fl.label}</div>
                                {fl.date
                                  ? <input aria-label={fl.label} style={inputStyle} type="date" min="1975-01-01" max={TODAY_DATE} value={claim[`${id}_${fl.key}`] || ''} onChange={e => set(`${id}_${fl.key}`, e.target.value)} />
                                  : <input aria-label={fl.label} style={inputStyle} placeholder={fl.label} value={claim[`${id}_${fl.key}`] || ''} onChange={e => set(`${id}_${fl.key}`, e.target.value)} />}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    }

                    const auto = deriveAnswer(id, claim, forSelf ? user : null);   // Bob 2026-10-17: never the officer's own profile on a member's claim
                    const val = (claim[id] || '').trim();
                    const ui = qaState[id];

                    // Collapsed: this question is settled -> one line, re-openable.
                    if (val && ui === 'done') {
                      return (
                        <div key={id} style={{ background: '#0A1628', border: '1px solid #2E6B4A', borderRadius: 6, padding: '10px 14px', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                          <span style={{ color: '#40C070', fontWeight: 700, fontSize: 13, marginTop: 1 }}>✓</span>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 12, color: '#8AA4BF', marginBottom: 2 }}>{q}</div>
                            <div style={{ fontSize: 14, color: '#F4F6F8', whiteSpace: 'pre-wrap' }}>{claim[id]}</div>
                          </div>
                          <button type="button" onClick={() => setQa(id, 'open')} style={{ background: 'none', border: 'none', color: '#4A9EE8', fontSize: 12, cursor: 'pointer' }}>Edit</button>
                        </div>
                      );
                    }

                    // Sourced from the claim, not yet answered -> show for confirmation.
                    if (!val && auto && auto.complete && ui !== 'open') {
                      return (
                        <div key={id} style={{ background: '#0A1628', border: '1px solid #2E6B4A', borderRadius: 6, padding: '14px 16px' }}>
                          <div style={{ fontSize: 14, color: '#F4F6F8', marginBottom: 8 }}>{q}</div>
                          <div style={{ background: '#0D2318', border: '1px solid #2E6B4A', borderRadius: 4, padding: '8px 12px', marginBottom: 8 }}>
                            <div style={{ fontSize: 10, color: '#40C070', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 3 }}>Found in your claim</div>
                            <div style={{ fontSize: 14, color: '#F4F6F8', whiteSpace: 'pre-wrap' }}>{auto.value}</div>
                          </div>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button type="button" className="btn btn-primary" style={{ fontSize: 11, background: '#27AE60' }}
                              onClick={() => { set(id, auto.value); setQa(id, 'done'); }}>✓ Confirm</button>
                            <button type="button" className="btn btn-secondary" style={{ fontSize: 11 }}
                              onClick={() => setQa(id, 'open')}>Type a different answer</button>
                          </div>
                        </div>
                      );
                    }

                    // Open: type or dictate a full answer.
                    return (
                      <div key={id} style={{ background: '#0A1628', border: `1px solid ${val ? '#2E6B4A' : '#2E4A6B'}`, borderRadius: 6, padding: '14px 16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                          <div style={{ fontSize: 14, color: '#F4F6F8' }}>{q}</div>
                          <DictationButton compact onText={t => appendTo(id, t)} />
                        </div>
                        {!val && getSuggestion(id) && (
                          <div style={{ background: '#0D1B2A', border: '1px dashed #2E6B4A', borderRadius: 6, padding: '8px 12px', marginBottom: 8 }}>
                            <div style={{ fontSize: 10, color: '#40C070', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 3 }}>&#128161; Suggested standard answer</div>
                            <div style={{ fontSize: 13, color: '#B8C4D0', marginBottom: 8, fontStyle: 'italic' }}>{getSuggestion(id)}</div>
                            <button type="button" className="btn btn-secondary" style={{ fontSize: 11 }} onClick={() => set(id, getSuggestion(id))}>Use this answer</button>
                          </div>
                        )}
                        <textarea aria-label="Your answer..."
                          style={{ width: '100%', background: '#0D1B2A', border: '1px solid #2E4A6B', borderRadius: 4, padding: '10px 12px', color: '#F4F6F8', fontSize: 15, resize: 'vertical', minHeight: 96, boxSizing: 'border-box' }}
                          placeholder="Your answer..."
                          value={claim[id] || ''}
                          onChange={e => set(id, e.target.value)}
                        />
                        {val && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                            <div style={{ fontSize: 10, color: '#40C070' }}>✓ Answered</div>
                            <button type="button" onClick={() => setQa(id, 'done')} style={{ background: 'none', border: 'none', color: '#4A9EE8', fontSize: 12, cursor: 'pointer' }}>Collapse ✓</button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* BOX 4 — Board Awards - ruling 276 screen 2: behind More (the General Chair adds awards; not needed to file) */}
            <MoreSection label="Board awards"><div className="card" style={{ marginBottom: 14 }}>
              <div className="card-header">
                <div className="card-title">Box 4 — Board Awards</div>
                <div style={{ fontSize: 11, color: '#8AA4BF' }}>PLB, 4th Division, SBA, NRAB — GC adds relevant awards</div>
              </div>
              {(claim.boardAwards || []).length === 0 && (
                <div style={{ color: 'var(--steel)', fontSize: 12, marginBottom: 10 }}>No board awards cited yet. The General Chair will add relevant awards as the case develops. You do not need to find awards to file your claim.</div>
              )}
              {(claim.boardAwards || []).map((a, i) => (
                <div key={i} style={{ background: '#0A1628', border: '1px solid #2E4A6B', borderRadius: 6, padding: '8px 12px', marginBottom: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#E8A020', marginRight: 8 }}>{a.boardType} Award #{a.awardNumber}</span>
                  <span style={{ fontSize: 12, color: '#F4F6F8' }}>{a.summary}</span>
                </div>
              ))}
              {(user?.role === 'general_chair' || user?.isAdmin) && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                  <select aria-label="New award" style={{ fontSize: 12, background: '#0D1B2A', border: '1px solid #2E4A6B', borderRadius: 4, padding: '5px 8px', color: '#F4F6F8' }}
                    value={newAward?.boardType || 'PLB'}
                    onChange={e => setNewAward(p => ({ ...p, boardType: e.target.value }))}>
                    {['PLB','4th Division','SBA','NRAB','Other'].map(t => <option key={t}>{t}</option>)}
                  </select>
                  <input aria-label="Award #" style={{ fontSize: 12, background: '#0D1B2A', border: '1px solid #2E4A6B', borderRadius: 4, padding: '5px 8px', color: '#F4F6F8', flex: 1 }}
                    placeholder="Award #" value={newAward?.awardNumber || ''}
                    onChange={e => setNewAward(p => ({ ...p, awardNumber: e.target.value }))} />
                  <input aria-label="Brief summary" style={{ fontSize: 12, background: '#0D1B2A', border: '1px solid #2E4A6B', borderRadius: 4, padding: '5px 8px', color: '#F4F6F8', flex: 2 }}
                    placeholder="Brief summary" value={newAward?.summary || ''}
                    onChange={e => setNewAward(p => ({ ...p, summary: e.target.value }))} />
                  <button className="btn btn-secondary" style={{ fontSize: 11 }}
                    onClick={() => { if (newAward?.awardNumber && newAward?.summary) { set('boardAwards', [...(claim.boardAwards || []), newAward]); setNewAward({}); }}}>
                    + Add Award
                  </button>
                </div>
              )}
            </div></MoreSection>

            {/* BOX 5 — Exhibits & Evidence */}
            <div className="card" style={{ marginBottom: 14 }}>
              <div className="card-header">
                <div className="card-title">Box 5 — Exhibits &amp; Evidence</div>
                <div style={{ fontSize: 11, color: '#8AA4BF' }}>Attach exhibit files, and list any exhibits you are not uploading here</div>
              </div>

              {/* SECTION 412 PART B: attach the actual files where the claim asks for exhibits. */}
              <ExhibitFileAttach files={exhibitFiles} onChange={setExhibitFiles} disabled={saving} />

              {/* RULING 250: SUGGESTED EXHIBITS - from the rules matched and the facts entered. Nothing is added until he presses + Add. */}
              {(() => {
                const listed = new Set((claim.exhibits || []).map(ex => ex.description));
                const sugg = suggestExhibits({ matchedRules, agreement, claim })
                  .map(sx => (BOX1_GUIDED ? { ...sx, description: cleanExhibitLabel(sx.description) } : sx))   // ruling 261, item 6
                  .filter(sx => !listed.has(sx.description));
                if (sugg.length === 0) return null;
                return (
                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 10, color: '#8AA4BF', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 4 }}>
                      Suggested exhibits
                    </div>
                    <div style={{ fontSize: 11, color: '#8AA4BF', marginBottom: 6 }}>
                      Based on the rules matched and the facts you entered. Add the ones that apply - attach the file above if you have it.
                    </div>
                    {sugg.map(sx => (
                      <div key={sx.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', background: '#0A1628', border: '1px solid #2E4A6B', borderRadius: 6, padding: '8px 12px', marginBottom: 6 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 12, color: '#F4F6F8' }}>{sx.description}</div>
                          <div style={{ fontSize: 11, color: '#8AA4BF' }}>{sx.why} Where: {sx.where}</div>
                        </div>
                        <button className="btn btn-secondary" style={{ fontSize: 11 }}
                          onClick={() => set('exhibits', [...(claim.exhibits || []), { description: sx.description }])}>
                          + Add
                        </button>
                      </div>
                    ))}
                  </div>
                );
              })()}

              <div style={{ borderTop: '1px solid #16283C', margin: '14px 0 10px' }} />
              <div style={{ fontSize: 10, color: '#8AA4BF', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 4 }}>
                List exhibits you are not uploading
              </div>
              <div style={{ fontSize: 11, color: '#8AA4BF', marginBottom: 6 }}>
                For an exhibit you will produce another way — a physical document, or one to be obtained. If you attached the file above, you do not need to list it here too.
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <input aria-label="Exhibit description (e.g. Timesheet for July 4, 2026)" style={{ fontSize: 12, background: '#0D1B2A', border: '1px solid #2E4A6B', borderRadius: 4, padding: '5px 8px', color: '#F4F6F8', flex: 2 }}
                  placeholder="Exhibit description (e.g. Timesheet for July 4, 2026)" value={newExhibit || ''}
                  onChange={e => setNewExhibit(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const desc = newExhibit?.trim();
                      if (desc) { set('exhibits', [...(claim.exhibits || []), { description: desc }]); setNewExhibit(''); }
                    }
                  }} />
                <button className="btn btn-secondary" style={{ fontSize: 11 }}
                  onClick={() => {
                    const desc = newExhibit?.trim();
                    if (desc) {
                      set('exhibits', [...(claim.exhibits || []), { description: desc }]);
                      setNewExhibit('');
                    }
                  }}>
                  + Add Exhibit
                </button>
              </div>
              {/* Auto-numbered exhibit list — builds itself below as exhibits are added */}
              <div style={{ background: '#0A1628', border: '1px solid #2E4A6B', borderRadius: 6, padding: '10px 12px', marginTop: 10 }}>
                <div style={{ fontSize: 10, color: '#8AA4BF', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 8 }}>
                  Exhibit List — {(claim.exhibits || []).length} item{(claim.exhibits || []).length === 1 ? '' : 's'}
                </div>
                {(claim.exhibits || []).length === 0 ? (
                  <div style={{ color: 'var(--steel)', fontSize: 12 }}>No exhibits yet. Each one you add appears here, numbered automatically.</div>
                ) : (
                  (claim.exhibits || []).map((ex, i) => (
                    <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '6px 0', borderTop: i === 0 ? 'none' : '1px solid #16283C' }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#E8A020', minWidth: 70 }}>Exhibit {i + 1}</span>
                      <span style={{ fontSize: 12, color: '#F4F6F8', flex: 1 }}>{ex.description}</span>
                      {ex.link && <a href={ex.link} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: '#4A9EE8' }}>View</a>}
                      <button onClick={() => set('exhibits', (claim.exhibits || []).filter((_, j) => j !== i))}
                        title="Remove exhibit"
                        style={{ background: 'none', border: 'none', color: '#C0392B', fontSize: 13, cursor: 'pointer', lineHeight: 1 }}>✕</button>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* RULING 251 (2026-09-29): the Internal Comments box is removed from Quick File. It was shown only to an
                officer filing his OWN claim (ruling 56), was displayed on no screen afterwards, was readable by the claim's
                owner in the rules despite its label, and conflicted with rulings 151-152. Stored comments stay on the record.
                Officer working notes belong on the LC/GC desk, built to 151-152. */}

            <ClaimBasisBoxes agreement={agreement} form={requirementsView} onSet={set} {...(BOX1_GUIDED ? { omit: PRESENTMENT_OMIT } : {})} />
            {/* Bob 2026-10-17, plan C: what is still needed, last, beside Continue - each open item names where it is answered. */}
            <EligibilityPayablePanel part="checklist" agreement={agreement} form={requirementsView} {...(BOX1_GUIDED ? { omit: PAYABLE_OMIT } : {})}
              where={{
                presentedTo: 'Claim Basis & Presentment, just above',
                proofOfDelivery: 'the claim page, once it is served',
                matchedRules: 'Box 2, the rule',
                claimantName: 'Step 1, Who are you?', employeeNumber: 'Step 1, Who are you?', seniorityDate: 'Step 1, Who are you?',
                incidentDate: 'Step 2, When did it happen?', eventLocation: 'Step 2, When did it happen?',
                amountClaimed: 'not asked on this screen',
              }} />

          </div>
        )}

        {/* STEP 1 — When */}
        {step === 1 && (
          <div>
            <div className="card">
              <div className="card-header"><div className="card-title">When did it happen?</div></div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Date *</label>
                  <input aria-label="Date" className="form-input" type="date" min="1975-01-01" max={TODAY_DATE} value={claim.incidentDate || ''} onChange={e => set('incidentDate', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Time of Incident</label>
                  <input aria-label="Time of Incident" className="form-input" type="time" value={claim.incidentTime12} onChange={e => set('incidentTime12', e.target.value)} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Where exactly did it occur?</label>
                {(() => {
                  const roster = rosterLocationsFor(claim.workLocation);
                  if (roster) {
                    return (
                      <React.Fragment>
                        <select aria-label="Where exactly did it occur?" className="form-select" value={claim.eventLocation || claim.workLocation || ''} onChange={e => set('eventLocation', e.target.value)}>
                          {roster.map(loc => <option key={loc} value={loc}>{loc}</option>)}
                        </select>
                        <div style={{ fontSize: 11, color: '#8AA4BF', marginTop: 4 }}>Your seniority covers these {roster.length} locations — pick where it occurred.</div>
                      </React.Fragment>
                    );
                  }
                  return (
                    <React.Fragment>
                      <input aria-label="Event location" className="form-input" placeholder={claim.workLocation || 'Location'} value={claim.eventLocation || claim.workLocation} onChange={e => set('eventLocation', e.target.value)} />
                      {claim.workLocation && <div style={{ fontSize: 11, color: '#8AA4BF', marginTop: 4 }}>Default: {claim.workLocation}</div>}
                    </React.Fragment>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {/* STEP 2 — Who was involved */}
        {step === 2 && (
          <div>
            <div className="card">
              <div className="card-header"><div className="card-title">Other employee involved (if any)</div></div>
              <div style={{ fontSize: 11, color: '#8AA4BF', marginBottom: 12 }}>{forSelf ? 'Not you - your own details are already on the claim.' : "Not the claimant - the claimant's details are already on the claim."} Anyone else whose work, assignment or treatment is part of what happened. Leave blank if no one else is involved. Fill what you know - your Local Chair can complete the rest. If more than one, note the others in Witnesses or the description.</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 12 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Name</label>
                  <input aria-label="Name" className="form-input" placeholder="Full name" value={claim.violatorName || ''} onChange={e => set('violatorName', e.target.value)} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Roster Number</label>
                  <input aria-label="Roster Number" className="form-input" placeholder="Roster #" value={claim.violatorRosterNumber || ''} onChange={e => set('violatorRosterNumber', e.target.value)} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Seniority Date</label>
                  <input aria-label="Seniority Date" className="form-input" type="date" min="1975-01-01" max={TODAY_DATE} value={claim.subjectSeniorityDate || ''} onChange={e => set('subjectSeniorityDate', e.target.value)} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Job Symbol / Assignment</label>
                  <input aria-label="Job Symbol / Assignment" className="form-input" placeholder="e.g. 0600-1400, Y091 East End" value={claim.violatorAssignment || ''} onChange={e => set('violatorAssignment', e.target.value)} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Hours of Assignment</label>
                  <input aria-label="Hours of Assignment" className="form-input" placeholder="e.g. 0700-1500" value={claim.violatorHours || ''} onChange={e => set('violatorHours', e.target.value)} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Assigned Rest Days</label>
                  <input aria-label="Assigned Rest Days" className="form-input" placeholder="e.g. Sat / Sun" value={claim.violatorRestDays || ''} onChange={e => set('violatorRestDays', e.target.value)} />
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1', marginBottom: 0, marginTop: 4, paddingTop: 10, borderTop: '1px solid #16283C' }}>
                  <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 400 }}>
                    <input aria-label="Hours of Service for this employee — for the record (not a claim factor)" type="checkbox" style={{ width: 'auto' }} checked={!!claim.hosFactorSubject} onChange={e => set('hosFactorSubject', e.target.checked)} />
                    Hours of Service for this employee — for the record (not a claim factor)
                  </label>
                  <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 400, marginTop: 6 }}>
                    <input aria-label="Hours of Service for this employee — for the record (not a claim factor)" type="checkbox" style={{ width: 'auto' }} checked={!!claim.hosFactorPosition} onChange={e => set('hosFactorPosition', e.target.checked)} />
                    …or a staffing shortfall on the position — for the record
                  </label>
                  {(claim.hosFactorSubject || claim.hosFactorPosition) && (
                    <textarea aria-label="…or a staffing shortfall on the position — for the record" className="form-input" rows={2} style={{ resize: 'vertical', marginTop: 6 }} placeholder="Hours on duty, rest status, or staffing-shortfall detail — the evidentiary record." value={claim.hosNoteSubjectPosition || ''} onChange={e => set('hosNoteSubjectPosition', e.target.value)} />
                  )}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Witnesses</label>
                <textarea aria-label="Witnesses" className="form-input" rows={4} style={{ resize: 'vertical', minHeight: 92 }} placeholder="Names and titles of any witnesses — list as many as apply, one per line (e.g. D. Kingsland, Yardmaster; R. Behrens, Trainmaster)" value={claim.witnesses || ''} onChange={e => set('witnesses', e.target.value)} />
              </div>
            </div>
          </div>
        )}

        {/* STEP 4 — Preview */}
        {step === 4 && (
          <div>
            <div style={{ background: '#0A1A0A', border: '1px solid #27AE60', borderRadius: 6, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: '#40C070' }}>
              <b>Ready to file?</b> Click <b>File Claim</b> to submit — or <b>Save as Draft</b> to finish later.
              Drafts are private to you. An administrator's read is logged. A draft does not stop your filing deadline.
            </div>
            <div className="card" style={{ marginBottom: 12 }}>
              <div className="card-header"><div className="card-title">Review Your Claim</div></div>
              {[
                ['Claimant', claim.claimantName],
                ['Railroad', claim.railroad],
                ['Work Location', claim.workLocation],
                ['Agreement', agreement],
                ['Local Committee', localCommittee],
                ['Incident Date', claim.incidentDate],
                ['Incident Time', claim.incidentTime12],
                ['Location of Event', claim.eventLocation || claim.workLocation],
                ['Other employee involved', claim.violatorName ? `${claim.violatorName} ${claim.violatorRosterNumber ? '(#' + claim.violatorRosterNumber + ')' : ''}` : '—'],
                ['Witnesses', claim.witnesses || '—'],
              ].map(([label, value]) => (
                <div key={label} style={{ display: 'flex', gap: 12, marginBottom: 6, fontSize: 12 }}>
                  <span style={{ color: '#8AA4BF', minWidth: 130 }}>{label}</span>
                  <span style={{ color: '#F4F6F8', fontWeight: 600 }}>{value}</span>
                </div>
              ))}
              <div style={{ marginTop: 10, padding: '8px 12px', background: '#0A1628', borderRadius: 6, fontSize: 12, color: '#F4F6F8', fontStyle: 'normal' }}>
                "{claim.description}"
              </div>
              {/* Section 510: the Box 3 answers, shown here so the member sees exactly what is filed. */}
              {collectBox3Answers(claim, allBox3Questions(matchedRules, claim.craft)).map(a => (
                <div key={a.id} style={{ marginTop: 8, fontSize: 12 }}>
                  <div style={{ color: '#8AA4BF' }}>{a.q}</div>
                  <div style={{ color: '#F4F6F8', fontWeight: 600, whiteSpace: 'pre-wrap' }}>{a.answer}</div>
                </div>
              ))}
            </div>
            {matchedRules.length > 0 && (
              <div className="card" style={{ marginBottom: 12 }}>
                <div className="card-header"><div className="card-title">Rule / basis of claim</div></div>
                {basisLabel ? <div style={{ fontSize: 11, color: '#FFD27A', margin: '0 0 8px' }}>{basisLabel}</div> : null}
                {matchedRules.map((r, i) => (
                  <div key={i} style={{ fontSize: 12, color: '#E8A020', fontWeight: 700, marginBottom: 4 }}>
                    {r.fullTitle ? (<>{r.rule} — <span style={{ color: '#8AA4BF', fontWeight: 400 }}>{r.fullTitle.split(' — ')[1]}</span></>) : r.rule}
                  </div>
                ))}
              </div>
            )}
            {/* Record 603: the bar warning again at Review & Submit - the last screen before File Claim. */}
            {BOX1_GUIDED && claim.claimKind && (
              <BarWarning bars={barsFor(agreement, claim.claimKind)} kindLabel={claimKind(claim.claimKind) ? claimKind(claim.claimKind).label : ''}
                style={{ marginTop: 0, marginBottom: 12 }} />
            )}
            {error && <div style={{ color: '#FF6B6B', fontSize: 12, marginBottom: 8 }}>⚠ {error}</div>}
          </div>
        )}

        {/* SECTION 536: the what-work box - on the page, not a browser window (test-no-blank-popups). */}
        {askWork ? (() => {
          const choose = (kind) => {
            const r = applyWorkKind({ agreement, matchedRules, kind, note: workNote });
            setMatchedRules(r.matchedRules);
            setClaim(c => ({ ...c, ...r.fields }));
            setAskWork(false); setWorkNote('');
            setStep(4);
          };
          return (
            <div role="dialog" aria-modal="true" aria-labelledby="wk-title" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
              <div style={{ background: '#0B1626', border: '1px solid #E8A020', borderRadius: 10, maxWidth: 560, width: '100%', padding: '18px 20px', maxHeight: '90vh', overflowY: 'auto' }}>
                <div id="wk-title" style={{ fontSize: 16, fontWeight: 700, color: '#F4F6F8', marginBottom: 6 }}>What work was it?</div>
                <div style={{ fontSize: 13, color: '#DCE7F2', marginBottom: 12, lineHeight: 1.5 }}>{workKindExplanation(agreement, matchedRules)}</div>
                {workKindChoices(agreement).map(k => (
                  <div key={k.key} style={{ border: '1px solid #2E4A6B', borderRadius: 6, padding: '10px 12px', marginBottom: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                      <div style={{ fontSize: 14, color: '#F4F6F8', fontWeight: 700 }}>{k.label}{k.rule ? <span style={{ color: '#E8A020', fontWeight: 400 }}> - {k.rule}</span> : null}</div>
                      {k.key !== 'other' ? <button className="btn btn-primary btn-sm" onClick={() => choose(k.key)}>This one</button> : null}
                    </div>
                    <div style={{ fontSize: 12, color: '#8AA4BF', marginTop: 3 }}>{k.hint}{k.why ? ' ' + k.why : ''}</div>
                    {k.key === 'other' ? (
                      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                        <input aria-label="What was it?" className="form-input" placeholder="What was it?" value={workNote} onChange={e => setWorkNote(e.target.value)} style={{ flex: 1 }} />
                        <button className="btn btn-secondary btn-sm" disabled={!workNote.trim()} onClick={() => choose('other')}>Use this</button>
                      </div>
                    ) : null}
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => setAskWork(false)}>Back to my description</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => choose('unsaid')}>I'm not sure - my Local Chairperson will ask me</button>
                </div>
              </div>
            </div>
          );
        })() : null}

        {/* Navigation */}
        <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'space-between' }}>
          <button className="btn btn-secondary" onClick={() => step > 0 ? setStep(step - 1) : onNavigate('claims')} style={{ minWidth: 100 }}>
            {step === 0 ? 'Cancel' : '← Back'}
          </button>
          {step < STEPS.length - 1 ? (
            <button className="btn btn-primary" onClick={() => {
              // SECTION 536: a junior-ahead claim, or one naming an article with no rule number, is asked what work it was
              // before it moves on - the claimant is the one person who knows.
              if (step === 3 && needsWorkKind({ matchedRules, description: claim.description, workKind: claim.workKind })) { setAskWork(true); return; }
              setStep(step + 1);
            }} disabled={!canProceed()} style={{ minWidth: 120, opacity: canProceed() ? 1 : 0.5 }}>
              Continue →
            </button>
          ) : (
            <React.Fragment>
              <button className="btn btn-secondary" onClick={handleSaveDraft} disabled={saving}
                style={{ minWidth: 130, borderColor: '#E8A020', color: '#E8A020' }}>
                {saving ? 'Saving...' : '💾 Save as Draft'}
              </button>
              <button className="btn btn-primary" onClick={handleSubmit} disabled={saving} style={{ minWidth: 140, background: '#27AE60' }}>
                {saving ? 'Filing...' : '✓ File Claim'}
              </button>
            </React.Fragment>
          )}
        </div>

      </div>
    </div>
  );
}
